import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { FireInputs, Scenario } from './types';
import { defaultInputs } from './defaults';
import { calculate } from './calculations';
import { ProfileSummary } from './components/ProfileManager';
import { ClientProfile } from './profileTypes';
import { supabase } from './services/supabaseClient';
import {
  listProfilesPaged,
  createProfile,
  getProfile,
} from './services/profileStorageSupabase';
import { useAutoSave } from './hooks/useAutoSave';
import ChartPanel from './components/ChartPanel';
import ProfileManager from './components/ProfileManager';
import EditModal from './components/EditModal';
import ToggleBar from './components/ToggleBar';
import InsightsPanel from './components/InsightsPanel';
import MilestoneTracker from './components/MilestoneTracker';
import ExportReport from './components/ExportReport';
import ScenarioPanel from './components/ScenarioPanel';
import NavBar from './components/NavBar';
import CoverageGapBar from './components/CoverageGapBar';
import FamilyImpactPanel from './components/FamilyImpactPanel';
import LandingPage from './components/LandingPage';
import { TeamProvider, useTeam } from './contexts/TeamContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { useToast } from './contexts/ToastContext';
import { createCheckoutSession, PRICES } from './services/subscriptionService';
import type { Session } from '@supabase/supabase-js';

// Route-level and conditionally-rendered components — code-split to reduce TTI
const PresentationMode    = React.lazy(() => import('./components/PresentationMode'));
const AdvisorDashboard    = React.lazy(() => import('./components/AdvisorDashboard'));
const ManagerDashboardPage = React.lazy(() => import('./components/ManagerDashboardPage'));
const ManagerDashboard    = React.lazy(() => import('./components/ManagerDashboard'));
const SettingsPage        = React.lazy(() => import('./components/SettingsPage'));
const PrivacyPolicy       = React.lazy(() => import('./components/PrivacyPolicy'));
const PlansPage           = React.lazy(() => import('./components/PlansPage'));
const TasksPage           = React.lazy(() => import('./components/TasksPage'));
const TrackPage           = React.lazy(() => import('./pages/TrackPage'));
const AdminPage           = React.lazy(() => import('./components/AdminPage'));
const ClientView          = React.lazy(() => import('./pages/ClientView'));

type BottomTab = 'none' | 'insights' | 'scenarios' | 'family';

function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}


function PageLoader() {
  return (
    <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ fontSize: 32 }}>🔥</div>
    </div>
  );
}

function UpgradeBanner({ message, onUpgrade }: { message: string; onUpgrade: () => void }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 500, background: '#1e1b4b', border: '1px solid #4f46e5',
      borderRadius: 12, padding: '14px 20px', display: 'flex',
      alignItems: 'center', gap: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      maxWidth: 480, width: 'calc(100vw - 48px)',
    }}>
      <span style={{ fontSize: 20 }}>⚡</span>
      <span style={{ flex: 1, fontSize: 13, color: '#c7d2fe', lineHeight: 1.5 }}>{message}</span>
      <button
        onClick={onUpgrade}
        style={{
          background: '#4f46e5', border: 'none', borderRadius: 8,
          color: '#fff', fontSize: 12, fontWeight: 700,
          padding: '8px 16px', cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Upgrade →
      </button>
    </div>
  );
}

function Dashboard() {
  const [activeProfile, setActiveProfile] = useState<ClientProfile | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [presenting, setPresenting] = useState(false);
  const [bottomTab, setBottomTab] = useState<BottomTab>('none');
  const [scenario, setScenario] = useState<Scenario>({ type: 'none', ageAtEvent: 35 });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [upgradeBanner, setUpgradeBanner] = useState('');
  const { isPro } = useSubscription();
  const { showError } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('fa-sidebar-open') !== 'true'
  );
  const [profileSummaries, setProfileSummaries] = useState<Record<string, ProfileSummary>>({});
  const [theme, toggleTheme] = useTheme();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;

  const isReadOnlyProfile = !!activeProfile?.userId
    && !!currentUserId
    && activeProfile.userId !== currentUserId;

  const { saveStatus, scheduleSave, flushSave, resetStatus: resetSaveStatus } = useAutoSave(isReadOnlyProfile, showError);

  // Load initial profile on mount
  useEffect(() => {
    // Safety timeout: if Supabase hangs, unblock the loading screen after 6s
    const timeout = setTimeout(() => setLoading(false), 6000);

    const loadProfiles = async () => {
      // Capture current user id once — used by EditModal to detect read-only views
      // (e.g. a manager viewing a teammate's client where RLS blocks writes).
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);

      // In local dev without auth session, delegate fully to localStorage backend
      if (isLocalDev) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setCurrentUserId('local-dev');
          // Falls through to the normal profile-loading path below, which now
          // reads from localStorage via the patched profileStorageSupabase functions.
        }
      }

      try {
        const { data: profiles } = await listProfilesPaged(0);
        const lastActiveId = localStorage.getItem('fire-active-profile');

        let profile: ClientProfile | null = null;
        if (lastActiveId) profile = profiles.find(p => p.id === lastActiveId) || null;
        if (!profile && profiles.length > 0) profile = profiles[0];
        if (!profile) profile = await createProfile('My First Client');

        setActiveProfile(profile);
        localStorage.setItem('fire-active-profile', profile.id);

        // Pre-compute on-track status for the first page of profiles
        const summaries: Record<string, ProfileSummary> = {};
        for (const p of profiles) {
          const r = calculate(p.inputs);
          summaries[p.id] = { onTrack: r.onTrack, wealthAtRetirement: r.wealthAtRetirement };
        }
        setProfileSummaries(summaries);
      } catch (e) {
        console.error('Failed to load profiles:', e);
        showError('Failed to load your clients. Please refresh the page.');
      }
      clearTimeout(timeout);
      setLoading(false);
    };
    loadProfiles();
    return () => clearTimeout(timeout);
  }, [showError]);

  const inputs = activeProfile?.inputs || defaultInputs;
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  const toggleExcluded = useCallback((id: string) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Filter inputs by excluded IDs before calculating
  const effectiveInputs = useMemo(() => ({
    ...inputs,
    purchases: inputs.purchases.filter(p => !excludedIds.has(p.id)),
    policies: inputs.policies.filter(p => !excludedIds.has(p.id)),
  }), [inputs, excludedIds]);

  const results = useMemo(() => calculate(effectiveInputs), [effectiveInputs]);

  // Detect an unconfigured client (still showing default values)
  const isDefaultClient = !activeProfile ||
    Math.abs(new Date(activeProfile.updatedAt).getTime() - new Date(activeProfile.createdAt).getTime()) < 5000;

  // Keep active client's on-track badge in sync as inputs change
  useEffect(() => {
    if (!activeProfile) return;
    const r = calculate(inputs); // use full inputs, not filtered
    setProfileSummaries(prev => ({
      ...prev,
      [activeProfile.id]: { onTrack: r.onTrack, wealthAtRetirement: r.wealthAtRetirement },
    }));
  }, [activeProfile, inputs]);

  // Scenario results (recalculated when scenario or inputs change)
  const scenarioResults = useMemo(() => {
    if (scenario.type === 'none') return null;
    return calculate(effectiveInputs, scenario);
  }, [effectiveInputs, scenario]);

  // Proposed plan — flip proposed policies to in-force for before/after toggle
  const proposedInputs = useMemo(() => {
    const hasProposed = effectiveInputs.policies.some(p => p.policyStatus === 'proposed');
    if (!hasProposed) return null;
    return {
      ...effectiveInputs,
      policies: effectiveInputs.policies.map(p =>
        p.policyStatus === 'proposed' ? { ...p, policyStatus: 'in-force' as const } : p
      ),
    };
  }, [effectiveInputs]);

  const proposedResults = useMemo(() =>
    proposedInputs ? calculate(proposedInputs) : null,
    [proposedInputs]
  );

  // Pre-compute +$X/month investment impact tiers for InsightsPanel
  const extraInvestmentTiers = useMemo(() =>
    ([200, 500, 1000] as const).map(extra => {
      const modInputs = {
        ...effectiveInputs,
        income: {
          ...effectiveInputs.income,
          annualInvestmentContribution: effectiveInputs.income.annualInvestmentContribution + extra * 12,
        },
      };
      const r = calculate(modInputs);
      return { extra, yearsToBuild: r.yearsToBuild, onTrack: r.onTrack, wealthAtRetirement: r.wealthAtRetirement };
    }),
    [effectiveInputs]
  );

  const proposedMonthlyPremium = useMemo(() => {
    if (!proposedInputs) return 0;
    return effectiveInputs.policies
      .filter(p => p.policyStatus === 'proposed')
      .reduce((sum, p) => {
        const annual = p.premiumFrequency === 'monthly' ? p.premiumAmount * 12
          : p.premiumFrequency === 'quarterly' ? p.premiumAmount * 4
          : p.premiumFrequency === 'semi-annual' ? p.premiumAmount * 2
          : p.premiumAmount;
        return sum + annual;
      }, 0) / 12;
  }, [effectiveInputs, proposedInputs]);

  const [showProposed, setShowProposed] = useState(false);

  // Keep scenario age in range when profile changes
  useEffect(() => {
    if (inputs.personal.currentAge > scenario.ageAtEvent) {
      setScenario(s => ({ ...s, ageAtEvent: inputs.personal.currentAge + 5 }));
    }
  }, [inputs.personal.currentAge, scenario.ageAtEvent]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  const handleInputChange = useCallback((newInputs: FireInputs) => {
    if (!activeProfile) return;
    const updated = { ...activeProfile, inputs: newInputs, updatedAt: new Date().toISOString() };
    setActiveProfile(updated);
    scheduleSave(updated);
  }, [activeProfile, scheduleSave]);

  const handleProfileMetaChange = useCallback((updates: Partial<Pick<ClientProfile, 'lastMeetingDate' | 'nextReviewDate' | 'notes' | 'noteEntries' | 'tags'>>) => {
    if (!activeProfile) return;
    const updated = { ...activeProfile, ...updates, updatedAt: new Date().toISOString() };
    setActiveProfile(updated);
    scheduleSave(updated);
  }, [activeProfile, scheduleSave]);

  const handleSelectProfile = useCallback((profile: ClientProfile) => {
    setActiveProfile(profile);
    setExcludedIds(new Set());
    localStorage.setItem('fire-active-profile', profile.id);
    resetSaveStatus();
  }, [resetSaveStatus]);

  const handleNewProfile = useCallback((profile: ClientProfile) => {
    setActiveProfile(profile);
    localStorage.setItem('fire-active-profile', profile.id);
    resetSaveStatus();
  }, [resetSaveStatus]);

  // Extend profileSummaries when ProfileManager loads additional pages
  const handleProfilesLoaded = useCallback((newProfiles: ClientProfile[]) => {
    setProfileSummaries(prev => {
      const next = { ...prev };
      for (const p of newProfiles) {
        if (!next[p.id]) {
          const r = calculate(p.inputs);
          next[p.id] = { onTrack: r.onTrack, wealthAtRetirement: r.wealthAtRetirement };
        }
      }
      return next;
    });
  }, []);

  // D2: reload active profile when another tab saves it
  const activeProfileRef = useRef(activeProfile);
  useEffect(() => { activeProfileRef.current = activeProfile; }, [activeProfile]);
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'fire-last-saved') return;
      try {
        const payload = JSON.parse(e.newValue ?? '');
        const current = activeProfileRef.current;
        if (current && payload.id === current.id) {
          getProfile(current.id).then(p => { if (p) setActiveProfile(p); }).catch(() => {});
        }
      } catch {}
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#111827', color: '#9ca3af' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔥</div>
          <div>Loading your clients...</div>
        </div>
      </div>
    );
  }

  // Presentation mode overlay
  if (presenting) {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <PresentationMode
          inputs={inputs}
          results={results}
          clientName={activeProfile?.name || 'Client'}
          onExit={() => setPresenting(false)}
        />
      </React.Suspense>
    );
  }

  const toggleTab = (tab: BottomTab) => {
    setBottomTab(prev => prev === tab ? 'none' : tab);
  };

  const lpa = inputs.estatePlanning?.lpa ?? false;
  const will = inputs.estatePlanning?.will ?? false;

  const toggleEstatePill = (field: 'lpa' | 'will') => {
    handleInputChange({
      ...inputs,
      estatePlanning: { ...inputs.estatePlanning, [field]: field === 'lpa' ? !lpa : !will },
    });
  };

  // Toolbar buttons for chart panel
  const chartToolbar = (
    <>
      <button
        onClick={() => {
          if (!isPro) { setUpgradeBanner('Insights is a Pro feature — upgrade to unlock AI-powered financial insights for every client.'); return; }
          toggleTab('insights');
        }}
        className="flex items-center gap-1.5"
        style={{
          background: bottomTab === 'insights' ? 'rgba(16, 185, 129, 0.15)' : 'var(--surface)',
          border: `1px solid ${bottomTab === 'insights' ? 'rgba(16, 185, 129, 0.4)' : 'var(--border)'}`,
          borderRadius: 8,
          color: bottomTab === 'insights' ? '#34d399' : isPro ? 'var(--text-2)' : 'var(--text-4)',
          padding: '8px 14px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        Insights{!isPro && ' ⚡'}
      </button>
      <button
        onClick={() => toggleTab('scenarios')}
        className="flex items-center gap-1.5"
        style={{
          background: bottomTab === 'scenarios' ? 'rgba(239, 68, 68, 0.15)' : 'var(--surface)',
          border: `1px solid ${bottomTab === 'scenarios' ? 'rgba(239, 68, 68, 0.4)' : 'var(--border)'}`,
          borderRadius: 8,
          color: bottomTab === 'scenarios' ? '#f87171' : 'var(--text-2)',
          padding: '8px 14px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        What If
      </button>
      <button
        onClick={() => toggleTab('family')}
        className="flex items-center gap-1.5"
        style={{
          background: bottomTab === 'family' ? 'rgba(251,146,60,0.15)' : 'var(--surface)',
          border: `1px solid ${bottomTab === 'family' ? 'rgba(251,146,60,0.4)' : 'var(--border)'}`,
          borderRadius: 8,
          color: bottomTab === 'family' ? '#fb923c' : 'var(--text-2)',
          padding: '8px 14px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
        Family
      </button>
      <button
        onClick={() => {
          if (!isPro) { setUpgradeBanner('Presentation mode is a Pro feature — upgrade to present client plans in full-screen mode.'); return; }
          setPresenting(true);
        }}
        className="flex items-center gap-1.5"
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          color: isPro ? 'var(--text-2)' : 'var(--text-4)', padding: '8px 14px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        Present{!isPro && ' ⚡'}
      </button>
      {/* Edit details — always accessible even when sidebar is hidden */}
      <button
        onClick={() => setEditModalOpen(true)}
        className="flex items-center gap-1.5"
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          color: 'var(--text-2)', padding: '8px 14px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Edit
      </button>
      {/* LPA / Will quick-toggle pills */}
      <button
        onClick={() => toggleEstatePill('lpa')}
        title={lpa ? 'LPA done — click to unmark' : 'LPA not done — click to mark done'}
        style={{
          background: lpa ? 'rgba(99,102,241,0.2)' : 'var(--card)',
          border: `1px solid ${lpa ? 'rgba(99,102,241,0.5)' : 'var(--border)'}`,
          borderRadius: 8, padding: '7px 11px', cursor: 'pointer',
          color: lpa ? '#818cf8' : 'var(--text-4)',
          fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
          transition: 'background 0.15s, color 0.15s, border-color 0.15s',
        }}
      >
        {lpa ? '✓ ' : ''}LPA
      </button>
      <button
        onClick={() => toggleEstatePill('will')}
        title={will ? 'Will done — click to unmark' : 'Will not done — click to mark done'}
        style={{
          background: will ? 'rgba(99,102,241,0.2)' : 'var(--card)',
          border: `1px solid ${will ? 'rgba(99,102,241,0.5)' : 'var(--border)'}`,
          borderRadius: 8, padding: '7px 11px', cursor: 'pointer',
          color: will ? '#818cf8' : 'var(--text-4)',
          fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
          transition: 'background 0.15s, color 0.15s, border-color 0.15s',
        }}
      >
        {will ? '✓ ' : ''}Will
      </button>
      <ExportReport
        inputs={inputs}
        results={results}
        clientName={activeProfile?.name || 'Client'}
      />
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{
          background: 'none', border: '1px solid var(--border)',
          borderRadius: 8, padding: '7px 10px', cursor: 'pointer',
          color: 'var(--text-3)', fontSize: 14, lineHeight: 1,
          display: 'flex', alignItems: 'center',
        }}
      >
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>
    </>
  );

  const isDrawerOpen = bottomTab !== 'none';

  return (
    <>
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text-1)', position: 'relative' }}>
      {/* Left sidebar: client list */}
      <div style={{
        width: sidebarCollapsed ? 0 : 280,
        minWidth: sidebarCollapsed ? 0 : 280,
        flexShrink: 0, height: '100vh',
        overflow: 'hidden',
        transition: 'width 0.25s ease, min-width 0.25s ease',
        position: 'relative',
      }}>
        <div style={{ width: 280, height: '100%' }}>
          <ProfileManager
            activeProfile={activeProfile}
            onSelectProfile={handleSelectProfile}
            onNewProfile={handleNewProfile}
            onEditDetails={() => setEditModalOpen(true)}
            saveStatus={saveStatus}
            profileSummaries={profileSummaries}
            onProfilesLoaded={handleProfilesLoaded}
          />
        </div>
      </div>

      {/* Sidebar toggle button */}
      <button
        onClick={() => setSidebarCollapsed(c => !c)}
        title={sidebarCollapsed ? 'Show client list' : 'Hide client list'}
        style={{
          position: 'absolute', left: sidebarCollapsed ? 8 : 268, top: 20, zIndex: 50,
          width: 28, height: 28, borderRadius: '50%',
          background: 'var(--card)', border: '1px solid var(--border)',
          color: 'var(--text-3)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          transition: 'left 0.25s ease',
        }}
      >
        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"
          style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Edit Details modal */}
      <EditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        inputs={inputs}
        onChange={handleInputChange}
        clientName={activeProfile?.name}
        currentProfileId={activeProfile?.id}
        profile={activeProfile}
        onProfileMetaChange={handleProfileMetaChange}
        onSaveNow={() => flushSave(activeProfile)}
        saveStatus={saveStatus}
        readOnly={isReadOnlyProfile}
      />

      {/* Right side: chart + toggleable bottom panel */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Chart panel */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {isDefaultClient && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
              padding: '16px 20px',
              background: 'linear-gradient(to bottom, var(--bg) 60%, transparent 100%)',
              pointerEvents: 'none',
            }}>
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pointerEvents: 'auto',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginBottom: 3 }}>
                    👋 Enter this client's details to see their real projection
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-4)' }}>
                    The chart is showing default placeholder values. Edit income, age, and assets to generate a personalised FIRE plan.
                  </div>
                </div>
                <button
                  onClick={() => setEditModalOpen(true)}
                  style={{
                    flexShrink: 0, marginLeft: 20,
                    background: '#10b981', border: 'none', borderRadius: 8,
                    color: '#fff', padding: '8px 16px', fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Edit Details →
                </button>
              </div>
            </div>
          )}
          <ChartPanel
            results={results}
            retirementAge={inputs.personal.retirementAge}
            toolbar={chartToolbar}
            scenarioResults={scenarioResults}
            isDark={theme === 'dark'}
            retirementExpenseItems={inputs.income.retirementExpenseItems}
            proposedResults={proposedResults}
            proposedMonthlyPremium={proposedMonthlyPremium}
            showProposed={showProposed}
            onToggleProposed={() => setShowProposed(v => !v)}
          />
        </div>

        {/* Coverage gap bar — always visible compact strip */}
        <CoverageGapBar inputs={inputs} compact />

        {/* Toggle bar: include/exclude individual purchases & policies */}
        <ToggleBar
          inputs={inputs}
          excludedIds={excludedIds}
          onToggle={toggleExcluded}
        />

        {/* Collapsible bottom drawer */}
        <div style={{
          maxHeight: isDrawerOpen ? 400 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
          borderTop: isDrawerOpen ? '1px solid var(--border)' : 'none',
          background: 'var(--deep)',
        }}>
          <div style={{ height: 360, overflowY: 'auto' }}>
            {bottomTab === 'insights' && (
              <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
                  <InsightsPanel
                    inputs={inputs}
                    results={results}
                    proposedResults={proposedResults}
                    hasProposed={proposedInputs !== null}
                    proposedMonthlyPremium={proposedMonthlyPremium}
                    extraInvestmentTiers={extraInvestmentTiers}
                  />
                </div>
                <div style={{ width: 300, flexShrink: 0, borderLeft: '1px solid var(--border)', overflowY: 'auto' }}>
                  <MilestoneTracker inputs={inputs} results={results} />
                </div>
              </div>
            )}
            {bottomTab === 'scenarios' && (
              <ScenarioPanel
                inputs={inputs}
                results={results}
                scenarioResults={scenarioResults}
                scenario={scenario}
                onScenarioChange={setScenario}
              />
            )}
            {bottomTab === 'family' && (
              <FamilyImpactPanel inputs={inputs} results={results} />
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Upgrade banner — shown when a starter user tries a Pro feature */}
    {upgradeBanner && <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 499 }} onClick={() => setUpgradeBanner('')} />
      <UpgradeBanner
        message={upgradeBanner}
        onUpgrade={async () => {
          setUpgradeBanner('');
          try {
            const url = await createCheckoutSession(PRICES.pro_monthly);
            window.location.href = url;
          } catch { window.location.href = '/settings'; }
        }}
      />
    </>}
    </>
  );
}


// Skip auth on localhost for dev/preview testing
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// ── Theme ─────────────────────────────────────────────────────────────────────
export type Theme = 'dark' | 'light';

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('fa-theme') as Theme) || 'dark'
  );
  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
    localStorage.setItem('fa-theme', theme);
  }, [theme]);
  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  return [theme, toggle];
}

function ProRoute({ children }: { children: React.ReactNode }) {
  const { isPro, loaded } = useSubscription();
  if (!loaded) return null;
  if (!isPro) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100%', gap: 16, padding: 32, textAlign: 'center',
        background: 'var(--bg)', color: 'var(--text-1)',
      }}>
        <span style={{ fontSize: 48 }}>⚡</span>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Pro feature</h2>
        <p style={{ margin: 0, color: 'var(--text-3)', maxWidth: 360, lineHeight: 1.6 }}>
          The Advisor Dashboard is available on Pro and Team plans.
          Upgrade to unlock team analytics, client overviews, and more.
        </p>
        <button
          onClick={() => window.location.href = '/settings'}
          style={{
            background: '#4f46e5', color: '#fff', border: 'none',
            borderRadius: 8, padding: '10px 24px', fontSize: 14,
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          View Plans →
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

const ADMIN_USER_ID = 'ef44569c-5216-4847-9b19-3b7797d13ea9';

function AdminRoute({ children }: { children: React.ReactNode }) {
  const [userId, setUserId] = React.useState<string | null | undefined>(undefined);
  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id ?? null));
  }, []);
  if (userId === undefined) return null; // loading
  if (userId !== ADMIN_USER_ID) {
    return (
      <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>Access denied</div>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

function DashboardRoute() {
  const { isManager, loaded } = useTeam();
  const { loaded: subLoaded } = useSubscription();
  if (!loaded || !subLoaded) return null;
  if (isManager) return <ManagerDashboardPage />;
  return <ProRoute><AdvisorDashboard /></ProRoute>;
}

function AppShell() {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const location = useLocation();
  useTheme();
  useTeam();

  useEffect(() => {
    const timeout = setTimeout(() => setChecking(false), 6000);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' && !session && window.location.hash.includes('access_token')) return;
      setSession(session);
      clearTimeout(timeout);
      setChecking(false);
    });
    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  if (location.pathname === '/privacy') {
    return (
      <React.Suspense fallback={null}>
        <PrivacyPolicy />
      </React.Suspense>
    );
  }

  if (checking) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#9ca3af' }}>
        <div style={{ fontSize: 40 }}>🔥</div>
      </div>
    );
  }

  if (!session) {
    return <LandingPage />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <NavBar />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <React.Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/team" element={<ManagerDashboard />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/track" element={<TrackPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          </Routes>
        </React.Suspense>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0f172a' }} />}>
        <Routes>
          {/* Public client-view route — no auth required */}
          <Route path="/client-view/:token" element={<ClientView />} />
          {/* All advisor routes — auth handled inside AppShell */}
          <Route path="/*" element={
            <SubscriptionProvider>
              <TeamProvider>
                <AppShell />
              </TeamProvider>
            </SubscriptionProvider>
          } />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}

export default App;
